// The agent-facing view of the same curriculum data the admin page manages: every curriculum and
// its trainings, annotated with whether *this* agent attended each session.
//
// The attendance flags are resolved server side by GET /curriculum/mine, which scopes itself to
// the signed-in user — no other agent's roster reaches the client, so there is nothing to filter
// out here and no need to work out who the current agent is.

import { flattenTrainings } from "../../../utils/curriculum";

/**
 * Flattens every training, carrying the `attended` / `attended_at` pair the endpoint already
 * resolved for the signed-in agent. `attended_at` is what the earned badge is dated by.
 */
export const decorateTrainings = (curricula) =>
	flattenTrainings(curricula).map((training) => ({
		...training,
		attended: Boolean(training.attended),
		attended_at: training.attended_at ?? null,
	}));

/**
 * The agent's earned badges — one row per training they attended, newest first, each carrying the
 * curriculum it sits under so the table can be read without opening anything.
 */
export const deriveAttendedTrainings = (decorated) =>
	decorated
		.filter((training) => training.attended)
		.sort((a, b) => String(b.attended_at ?? "").localeCompare(String(a.attended_at ?? "")));

/**
 * Per-curriculum attendance tally, keyed by curriculum id, for the "Attended" column.
 */
export const countAttendedByCurriculum = (decorated) => {
	const counts = new Map();

	decorated.forEach((training) => {
		if (!training.attended) return;
		counts.set(training.curriculumId, (counts.get(training.curriculumId) ?? 0) + 1);
	});

	return counts;
};
